import { useContext, useEffect, useState, useRef } from 'react'
import styles from './GlobalNewsletterModal.module.scss'
import { GlobalSettingsContext } from '@/context/GlobalSettings'
import useI18n from '@/hooks/use-i18n'
import NewsletterForm from './NewsletterForm'
import Header from './Header'
import Subtitle from '@/components/GlobalNewsletterModal/Subtitle'
import Image from 'next/image'
import useBreakpoint from '@/hooks/use-breakpoint'

const COOKIE_NAME = 'newsletter_modal_dismissed'
const COOKIE_EXPIRY_DAYS = 7
const SUCCESS_AUTO_DISMISS_DELAY = 5000 // 5 seconds

const GlobalNewsletterModal = () => {
  const { newsletterModalData } = useContext(GlobalSettingsContext)
  const { isEnabled, delayTime, content } = newsletterModalData || {}
  const { eyebrow, header, subtitle, successMessage, showWhatToExpect } = content || {}
  const [isVisible, setIsVisible] = useState(false)
  const [formSuccess, setFormSuccess] = useState(false)
  const overlayRef = useRef<HTMLDivElement>(null)
  const { i18n } = useI18n()
  const { isMobile } = useBreakpoint()

  const t = (key: string) => i18n(key)

  useEffect(() => {
    // Don't show if disabled or already dismissed
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${COOKIE_NAME}=`))
      ?.split('=')[1]

    if (!isEnabled || cookieValue) {
      return
    }

    // Show modal after delay
    const timer = setTimeout(() => {
      // Save scroll position before locking
      const scrollY = window.scrollY
      document.body.style.top = `-${scrollY}px`
      setIsVisible(true)
      document.body.classList.add('global-modal-open')
    }, (delayTime || 0) * 1000)

    return () => clearTimeout(timer)
  }, [isEnabled, delayTime])

  useEffect(() => {
    // Auto-dismiss after showing success message
    if (formSuccess) {
      const timer = setTimeout(() => {
        handleDismiss()
      }, SUCCESS_AUTO_DISMISS_DELAY)

      return () => clearTimeout(timer)
    }
  }, [formSuccess])

  // Enable wheel scrolling on the overlay
  useEffect(() => {
    if (!overlayRef.current || !isVisible) return

    const handleWheel = (e: WheelEvent) => {
      if (!overlayRef.current) return

      // Prevent the default scroll behavior
      e.preventDefault()

      // Manually scroll the overlay
      overlayRef.current.scrollTop += e.deltaY
    }

    const overlay = overlayRef.current
    overlay.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      overlay.removeEventListener('wheel', handleWheel)
    }
  }, [isVisible])

  const handleDismiss = () => {
    setIsVisible(false)
    document.body.classList.remove('global-modal-open')
    // Restore scroll position
    const scrollY = parseInt(document.body.style.top || '0', 10) * -1
    document.body.style.top = ''
    window.scrollTo(0, scrollY)
    // Set cookie to expire in 7 days
    const expiryDate = new Date()
    expiryDate.setDate(expiryDate.getDate() + COOKIE_EXPIRY_DAYS)
    document.cookie = `${COOKIE_NAME}=true; expires=${expiryDate.toUTCString()}; path=/`
  }

  const handleFormSuccess = () => {
    setFormSuccess(true)
  }

  if (!isEnabled || !isVisible || !content) {
    return null
  }

  return (
    <div
      ref={overlayRef}
      className={`${styles.overlay} overlay-scrollable`}
      onClick={(e) => e.target === e.currentTarget && handleDismiss()}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.baoBackground}>
          <Image
            src="/images/baobao-background.png"
            alt=""
            width={isMobile ? 90 : 336}
            height={isMobile ? 83 : 317}
            aria-hidden="true"
          />
        </div>
        <button className={styles.closeButton} onClick={handleDismiss} aria-label={t('close')} />
        <div className={styles.content}>
          {!formSuccess && <>
            {eyebrow && <div className={styles.eyebrow}>{eyebrow}</div>}
            {header && <Header content={header} />}
            {subtitle && <Subtitle content={subtitle} />}
          </>}

          <NewsletterForm
            onDismiss={handleFormSuccess}
            successMessage={{
              title: successMessage?.title || [],
              subtitle: successMessage?.subtitle || [],
            }}
            hasWhatToExpect={showWhatToExpect}
          />
        </div>
      </div>
    </div>
  )
}

GlobalNewsletterModal.displayName = 'GlobalNewsletterModal'

export default GlobalNewsletterModal
